import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFacebookPage } from '../contexts/FacebookPageContext';
import { apiClient } from '../lib/axios-interceptor';

interface PageRouteGuardProps {
  children: React.ReactNode;
}

// Route suffixes scoped to a specific page. Keep in sync with App.tsx and
// FacebookPageSelector. `/comments` and `/review` used to be missing here,
// which let a stale or disconnected page id sail straight through the guard.
const PAGE_SCOPED = ['comments', 'moderation', 'moderation-rules', 'review', 'autoreply'];
const PAGE_ROUTE_RE = new RegExp(`^/accounts/([^/]+)/(${PAGE_SCOPED.join('|')})(?:/|$)`);

const PageRouteGuard: React.FC<PageRouteGuardProps> = ({ children }) => {
  const { isPageSelected, selectedPage, setSelectedPage, clearSelectedPage, isLoading } = useFacebookPage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't run guard logic while still loading
    if (isLoading) return;

    // Extract pageId from URL if this is a page-scoped route
    const routeMatch = location.pathname.match(PAGE_ROUTE_RE);
    const pageIdFromUrl = routeMatch ? routeMatch[1] : null;
    const isPageSpecificRoute = !!routeMatch;

    // If we're on a page-specific route
    if (isPageSpecificRoute) {
      // If we have a pageId in URL but no selected page, try to fetch and set it
      if (pageIdFromUrl && (!selectedPage || selectedPage.id !== pageIdFromUrl)) {
        console.log(`🔄 [PAGE_GUARD] Page ID from URL: ${pageIdFromUrl}, but no matching selected page`);
        
        // Try to fetch page data from API and set it as selected
        fetchAndSetPageFromUrl(pageIdFromUrl);
        return;
      }

      // If we don't have a selected page and can't get one from URL, redirect
      if (!isPageSelected && !pageIdFromUrl) {
        console.log(`🚫 [PAGE_GUARD] No page selected and no page ID in URL, redirecting to page selection`);
        navigate('/accounts', { 
          replace: true,
          state: { 
            from: location.pathname,
            message: 'Please select a Facebook page to continue'
          }
        });
        return;
      }
    }

    // Handle other routes that might need page selection
    const otherPageRoutes = ['/moderation', '/autoreply'];
    const requiresPageSelection = otherPageRoutes.some(route => 
      location.pathname.includes(route) && !location.pathname.includes('/accounts/')
    );

    if (requiresPageSelection && !isPageSelected) {
      console.log(`🚫 [PAGE_GUARD] Route requires page selection, redirecting`);
      navigate('/accounts', { 
        replace: true,
        state: { 
          from: location.pathname,
          message: 'Please select a Facebook page to continue'
        }
      });
    }
  }, [isPageSelected, selectedPage, location.pathname, navigate, isLoading]);

  const fetchAndSetPageFromUrl = async (pageId: string) => {
    try {
      console.log(`🔍 [PAGE_GUARD] Fetching page data for ID: ${pageId}`);
      
      // Try to get page data from API using apiClient (automatically includes Auth0 token)
      const response = await apiClient.get(`/api/facebook/pages/${pageId}`);
      
      const pageData = response.data;
      console.log(`✅ [PAGE_GUARD] Successfully fetched page data:`, pageData);

      // The detail endpoint answers 200 for disconnected pages, so a 404 check
      // alone would happily select a page the user can no longer work on.
      if (pageData?.isConnected === false) {
        console.warn(`⚠️ [PAGE_GUARD] Page ${pageId} is disconnected, redirecting`);
        clearSelectedPage();
        navigate('/accounts', {
          replace: true,
          state: {
            from: location.pathname,
            message: 'That page is disconnected. Reconnect it or pick another one.'
          }
        });
        return;
      }

      setSelectedPage({
        id: pageData.id,
        name: pageData.name,
        picture: pageData.picture?.data?.url || pageData.picture,
        access_token: pageData.access_token,
        platform: pageData.platform || selectedPage?.platform,
        username: pageData.username
      });
      
    } catch (error: any) {
      console.error(`❌ [PAGE_GUARD] Error fetching page data:`, error);
      
      if (error.response?.status === 404) {
        console.warn(`⚠️ [PAGE_GUARD] Page not found, redirecting to page selection`);
        navigate('/accounts', { 
          replace: true,
          state: { 
            from: location.pathname,
            message: 'Page not found. Please select a valid Facebook page.'
          }
        });
      } else {
        console.warn(`⚠️ [PAGE_GUARD] Failed to fetch page data, redirecting to page selection`);
        navigate('/accounts', { 
          replace: true,
          state: { 
            from: location.pathname,
            message: 'Error loading page. Please select a Facebook page.'
          }
        });
      }
    }
  };

  // Show loading state briefly to prevent flash
  if (isLoading) {
    return <div className="flex items-center justify-center p-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
    </div>;
  }

  return <>{children}</>;
};

export default PageRouteGuard; 